import { Routes } from "@/constants/routes";
import type { DashboardChecklist, DashboardProducts } from "@/types/dashboard";
import { Icon } from "@uigovpe/components";
import { useNavigate } from "react-router-dom";

type Props = {
  checklist?: DashboardChecklist;
  products?: DashboardProducts;
  onDismiss?: () => void;
};

export default function OnboardingProgress({
  checklist,
  products,
  onDismiss,
}: Props) {
  const navigate = useNavigate();
  const steps = [
    ["Validar E-mail", checklist?.emailVerificado],
    ["Cadastrar dados do perfil", checklist?.perfilCompleto],
    ["Cadastrar primeiro produto", checklist?.possuiProdutos],
    ["Publicar seu produto", checklist?.possuiProdutosPublicados],
    [
      "Compartilhar perfil nas redes sociais",
      checklist?.compartilhouRedesSociais,
    ],
  ] as const;
  const completed = steps.filter(([, done]) => Boolean(done)).length;
  const remaining = steps.length - completed;
  const percentage = Math.round((completed / steps.length) * 100);
  // const percentage: number = 100
  const circumference = 2 * Math.PI * 40;

  if (percentage === 100) {
    const showcaseIsActive = Boolean(checklist?.possuiProdutosPublicados);

    return (
      <div className="relative flex h-full min-h-[280px] flex-col items-center justify-center rounded-2xl bg-[#f8fafc] p-5 shadow-xl">
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fechar status do perfil"
          className="absolute! top-5! right-5! flex! cursor-pointer! items-center! justify-center! border-0! bg-transparent! p-1! text-[#4e3873]!"
        >
          <Icon icon="close" outline className="text-[18px]!" />
        </button>

        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f3f0ff]">
            <Icon
              icon="check_circle"
              className="text-[22px]! text-[#4e3873]!"
            />
          </span>
          <h3 className="m-0! text-[16px]! leading-[24px]! font-bold! text-[#494c57]!">
            Perfil concluído
          </h3>
        </div>

        <div className="w-full overflow-hidden rounded-md border border-[#d4cef7] bg-white">
          <div className="flex min-h-9 items-center gap-2 border-b border-[#d4cef7] px-3 text-[12px]! text-[#494c57]!">
            <Icon
              icon="check_circle"
              outline
              className="text-[16px]! text-[#4e3873]!"
            />
            <span className="flex-1">Status do perfil</span>
            <strong className="text-[#4e3873]!">100% concluído</strong>
          </div>
          <div className="flex min-h-9 items-center gap-2 border-b border-[#d4cef7] px-3 text-[12px]! text-[#494c57]!">
            <Icon
              icon="shopping_bag"
              outline
              className="text-[16px]! text-[#4e3873]!"
            />
            <span className="flex-1">Produtos Publicados</span>
            <strong className="text-[#4e3873]!">
              {products?.visiveis_vitrine ?? 0}
            </strong>
          </div>
          <div className="flex min-h-9 items-center gap-2 px-3 text-[12px]! text-[#494c57]!">
            <Icon
              icon="storefront"
              outline
              className="text-[16px]! text-[#4e3873]!"
            />
            <span className="flex-1">Vitrine</span>
            <strong
              className={
                showcaseIsActive ? "text-[#56c02b]!" : "text-[#6b7280]!"
              }
            >
              <span
                className={`mr-1 inline-block h-2 w-2 rounded-full ${showcaseIsActive ? "bg-[#56c02b]" : "bg-[#6b7280]"}`}
              />
              {showcaseIsActive ? "Ativa" : "Inativa"}
            </strong>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[280px] flex-col rounded-2xl bg-[#f8fafc] p-6 shadow-xl max-sm:p-4">
      <div className="mb-5 flex items-center gap-7 max-sm:mb-4 max-sm:flex-wrap max-sm:gap-x-3 max-sm:gap-y-1">
        <h3 className="m-0! text-[18px]! leading-[24px]! font-bold! text-[#494c57]! max-sm:text-[1rem]!">
          Próximos Passos
        </h3>
        <span className="text-[14px]! leading-[20px]! font-normal! text-[#494c57]!">
          {completed} / {steps.length} concluídos
        </span>
      </div>

      <div className="flex flex-1 items-start justify-between gap-4 max-sm:flex-col max-sm:gap-7">
        <div className="flex min-w-0 flex-1 flex-col gap-4 max-sm:order-2 max-sm:w-full max-sm:gap-3">
          {steps.map(([label, done]) => (
            <div
              key={label}
              className="flex items-center gap-2 max-sm:items-start"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#4e3873] ${done ? "bg-[#4e3873]" : "bg-transparent"}`}
              >
                {done && (
                  <Icon icon="check" className="text-[14px]! text-white!" />
                )}
              </span>
              <span className="text-[13px]! leading-[25px]! font-medium! text-[#4e3873]! max-sm:leading-[1.35]!">
                {label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex w-40 -translate-y-[30px] shrink-0 flex-col items-center text-center max-sm:order-1 max-sm:mt-4 max-sm:grid max-sm:w-full max-sm:translate-y-0 max-sm:grid-cols-[6rem_minmax(0,1fr)] max-sm:grid-rows-[repeat(3,auto)] max-sm:gap-x-3 max-sm:text-left">
          <div className="relative h-32 w-32 max-sm:row-span-3 max-sm:h-24 max-sm:w-24 max-sm:self-center">
            <svg
              className="h-full w-full -rotate-90"
              viewBox="0 0 100 100"
              aria-hidden="true"
            >
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#dedede"
                strokeWidth="11"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke="#4e2d73"
                strokeWidth="11"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={
                  circumference - (percentage / 100) * circumference
                }
              />
            </svg>
            <strong className="absolute inset-0 flex items-center justify-center text-[26px]! font-bold! text-[#4e3873]!">
              {percentage}%
            </strong>
          </div>
          <p className="mt-3! mb-0! text-[15px]! leading-[20px]! font-medium! text-[#494c57]! max-sm:mt-0! max-sm:self-end max-sm:justify-self-start">
            Perfil Incompleto
          </p>
          <p className="m-0! max-w-36 text-[12px]! leading-[17px]! font-normal! text-[#494c57]! max-sm:justify-self-start">
            Faltam {remaining} {remaining === 1 ? "etapa" : "etapas"} para
            finalizar o seu perfil
          </p>
          <button
            type="button"
            onClick={() => navigate(Routes.PROFILE)}
            className="mt-2! flex cursor-pointer items-center gap-1 border-0! bg-transparent! p-0! text-[12px]! leading-[18px]! font-medium! text-[#4e3873]! max-sm:self-start max-sm:justify-self-start"
          >
            Continuar cadastro{" "}
            <Icon icon="arrow_right_alt" outline className="text-[16px]!" />
          </button>
        </div>
      </div>
    </div>
  );
}
